import {useState, useEffect, Dispatch, SetStateAction, ChangeEvent} from 'react'
import {handleEsc, RequestResult} from '../../lib'
import {createDispatchActions, selectTheme} from '../../store'
import {Input, Textarea, LabelBox, PopupTemplate, PopupSize, ParticipantPopupOperationType, ButtonReverse} from '..'
import {IParticipant} from '../../shared/db'

export const ParticipantPopup = (props: {
  closeAction: () => void
  method: string
  participant?: IParticipant | null
  setSearchValue?: Dispatch<SetStateAction<string>>
  setOperationType?: Dispatch<SetStateAction<ParticipantPopupOperationType | null>>
  projectId?: string
  batchId?: string
}) => {
  const {doREQUEST_PARTICIPANT_CREATE, doREQUEST_PARTICIPANT_UPDATE, doADHERENCE_DELETE} = createDispatchActions()
  const {closeAction, method, setSearchValue, setOperationType, projectId, batchId, participant} = props
  const {color, fontWeight, fontSize} = selectTheme()

  handleEsc(closeAction)

  const [requestResultOfIdOfParticipantCreate, setRequestResultOfIdOfParticipantCreate] =
    useState<RequestResult | null>(null)
  const [requestResultOfIdOfParticipantUpdate, setRequestResultOfIdOfParticipantUpdate] =
    useState<RequestResult | null>(null)

  const [participantName, setParticipantName] = useState<string>(participant?.insignia ?? '')
  const [participantNote, setParticipantNote] = useState<string>(participant?.note ?? '')

  const onSubmit = () => {
    if (method === 'Add') {
      // Invalidate adherence so digest-related views refetch; only needed when roster changes.
      // Edit must not clear: mobile type / app version read from adherence, and this page does not
      // re-run project-participant-list while projectId is unchanged, so the table would show "--".
      doADHERENCE_DELETE({projectId})
      doREQUEST_PARTICIPANT_CREATE({
        setRequestResult: setRequestResultOfIdOfParticipantCreate,
        payload: {
          batchId,
          insignia: participantName,
          note: participantNote === '' ? undefined : participantNote, // backend not allow empty string
        },
      })
    } else if (method === 'Edit') {
      doREQUEST_PARTICIPANT_UPDATE({
        setRequestResult: setRequestResultOfIdOfParticipantUpdate,
        payload: {
          participantId: participant?.id,
          insignia: participantName,
          note: participantNote === '' ? ' ' : participantNote, // backend not allow empty string
        },
      })
    }
  }

  useEffect(() => {
    if (!requestResultOfIdOfParticipantCreate && !requestResultOfIdOfParticipantUpdate) return
    if (method === 'Add') setSearchValue?.('')
    closeAction()
  }, [requestResultOfIdOfParticipantCreate, requestResultOfIdOfParticipantUpdate])

  const handleDeleteParticipant = () => {
    setOperationType?.(ParticipantPopupOperationType.Delete)
  }

  const handleCompleteDataCollection = () => {
    setOperationType?.(ParticipantPopupOperationType.CompleteDataCollection)
  }

  const shouldRenderResumeDataCollectionSection =
    participant?.completedDate != null || participant?.markCompletedAt != null

  return (
    <PopupTemplate
      {...{
        title: `${method} Participant`,
        description: '',
        background: true,
        popupSize: PopupSize.Medium,
        handleClosePopup: closeAction,
        mainFunction: onSubmit,
        buttonContent: method === 'Add' ? 'Add' : 'Save',
        buttonDisabled: participantName.length === 0,
      }}>
      <div>
        <LabelBox
          {...{
            label: 'Participant ID',
            leftCharacters: participantName ? 50 - participantName?.length : 50,
            required: true,
          }}>
          <Input
            style={{marginBottom: '32px'}}
            placeholder='Use a unique and anonymous identifier'
            maxLength='50'
            value={participantName}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setParticipantName(e.target.value)}
            autoFocus={method === 'Add'}
          />
        </LabelBox>

        <LabelBox
          {...{
            label: 'Notes',
            leftCharacters: 250 - participantNote?.length,
          }}>
          <Textarea
            rows={3}
            placeholder='Add internal notes here'
            maxLength={250}
            value={participantNote}
            onChange={(e) => setParticipantNote(e.target.value)}
          />
        </LabelBox>
        {method === 'Edit' && (
          <div
            css={{
              marginTop: '24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
            }}>
            <div>
              <span
                css={{
                  fontSize: fontSize.h5,
                  fontWeight: fontWeight.medium,
                  color: color.textIcon.title,
                }}>
                Danger Zone
              </span>
              <div
                css={{
                  marginTop: '16px',
                  height: '1px',
                  backgroundColor: color.border.default,
                }}
              />
            </div>
            <div
              css={{
                display: 'flex',
                flexDirection: 'column',
                gap: '0',
              }}>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                }}>
                <span
                  css={{
                    fontSize: fontSize.h6,
                    fontWeight: fontWeight.medium,
                    color: color.textIcon.title,
                  }}>
                  {shouldRenderResumeDataCollectionSection ? 'Resume Data Collection' : 'Complete data collection'}
                </span>
                <span
                  css={{
                    fontSize: fontSize.h6,
                    color: color.textIcon.secondary,
                  }}>
                  {shouldRenderResumeDataCollectionSection
                    ? 'Allow this participant to log in again and continue uploading data.'
                    : 'Finalize data collection for this participant. The participant will be logged out immediately and will not be able to upload additional data.'}
                </span>
                <ButtonReverse
                  onClick={handleCompleteDataCollection}
                  btnColor={shouldRenderResumeDataCollectionSection ? 'primary' : 'warning'}
                  btnPadding='mediumSmall'
                  css={{marginTop: '8px'}}>
                  {shouldRenderResumeDataCollectionSection ? 'Resume Data Collection' : 'Complete data collection now'}
                </ButtonReverse>
              </div>
              <div
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '8px',
                  marginTop: '40px',
                }}>
                <span
                  css={{
                    fontSize: fontSize.h6,
                    fontWeight: fontWeight.medium,
                    color: color.textIcon.title,
                  }}>
                  Delete participant
                </span>
                <span
                  css={{
                    fontSize: fontSize.h6,
                    color: color.textIcon.secondary,
                  }}>
                  The participant will be logged out immediately, and all previously collected data will be permanently
                  deleted.
                </span>
                <ButtonReverse
                  onClick={handleDeleteParticipant}
                  btnColor='warning'
                  btnPadding='mediumSmall'
                  css={{marginTop: '8px'}}>
                  Delete Participant
                </ButtonReverse>
              </div>
            </div>
          </div>
        )}
      </div>
    </PopupTemplate>
  )
}
